/**
 * ReportView Component
 *
 * Renders the research report with clickable citation markers.
 * Citations use the format [label]{ref:marker_id} in the source text,
 * which are transformed to clickable links that resolve through
 * markers → findings for source inspection.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronDown,
  Download,
  ExternalLink,
  FileText,
  Info,
  Loader2,
  Pencil,
  Presentation,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import { DictReport, DictMarker, DictFinding, GroundingFinding } from '../../types/workspaceState';
import {
  preprocessDocumentMarkdown,
  buildBaseMarkdownComponents,
} from './reportMarkdown';
import {
  useProjectStore,
  normalizeGroundingFindings,
  groundingReviewProgress,
  groundingEffectiveCounts,
  groundingRepairedRows,
} from '../../stores/projectStore';
import GroundingReviewDrawer, { ReviewRow } from './GroundingReviewDrawer';
import {
  GROUNDING_ACTION_LINES,
  GROUNDING_CARD_FOOTER,
  groundingCardCopy,
} from './groundingCardCopy';
import { submitGroundingReview, GroundingReviewDecisionInput } from '../../apiService';
import ResearchWelcomePanel from '../process/ResearchWelcomePanel';
import {
  exportWorkspaceReportPdf,
  exportWorkspaceReportDocx,
  exportWorkspaceReportPptx,
  updateWorkspaceReportContent,
} from '../../apiService';
import ReportEditor from './ReportEditor';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useToast } from '../ui/Toast';
import type { OnlineSupport as OnlineSupportItem } from '../../types/online_support';
import { formatCount } from '../../utils/plural';

interface ReportViewProps {
  report: DictReport | null;
  draftReport: string | null;
  markers: Record<string, DictMarker>;
  findings: Record<string, DictFinding>;
  onCitationClick: (findingId: string, markerId: string) => void;
  // BAR-39: online support references — [label]{online:online_id} chips.
  // When present, the report renders OnlineSupport chips distinct from
  // regulatory citation chips (globe icon, neutral colour). Click dispatches
  // the online_support_id via onOnlineRefClick so the parent can open the
  // OnlineSupport in the unified findings panel.
  onlineSupport?: Record<string, OnlineSupportItem>;
  onOnlineRefClick?: (onlineSupportId: string) => void;
  /** The finding / online-support currently selected in the findings
   *  panel (driven by clicking a chip here or a row there). The matching
   *  report chip is highlighted light blue so you can see at a glance
   *  which evidence you opened, even after the panel reflows the report. */
  selectedFindingId?: string | null;
  selectedOnlineSupportId?: string | null;
  // BAR-34 — when all three are present, the report toolbar shows the
  // export menu (PDF / Word / PowerPoint / Google Docs) and downloads via the report
  // library service. All optional so call sites that haven't wired the
  // workspace context yet keep working; `authToken` is required by the
  // backend's `require_auth` dependency on the export endpoints.
  workspaceId?: string;
  userId?: string;
  authToken?: string;
  /** BAR-192 Phase E — dispatches a pre-filled instruction into the
   *  workspace chat (the Fix button's action). Wired from App.tsx's
   *  handleSendMessage; when absent the Fix button is hidden. */
  onDispatchChatMessage?: (text: string, opts?: { repair?: boolean }) => void;
  /** Project is truly fresh — no report, no graph nodes, no findings.
   *  When true the empty state is the Welcome-to-Research walkthrough.
   *  When false-and-still-empty, research is in flight and we show a
   *  short in-flight placeholder instead. Defaults to false so the
   *  in-flight spinner is the safe fallback for any call site that
   *  forgets to wire the prop — the welcome tutorial only renders when
   *  the parent explicitly opts in. */
  isWelcomeState?: boolean;
}

type ExportFormat = 'PDF' | 'Word' | 'PowerPoint' | 'Google Docs';

const REPORT_HEADING_RE = /^(#{1,6})\s+(.+?)\s*$/;

export const REPORT_EXECUTIVE_SUMMARY_REQUIRED_MESSAGE =
  'Reports must start with an Executive Summary section. Add ## Executive Summary ' +
  'immediately after the optional title, with no subtitle or preamble before it.';

export function hasExecutiveSummaryAtTop(content: string): boolean {
  const lines = content.split(/\r?\n/);
  let index = 0;
  while (index < lines.length && !lines[index].trim()) {
    index += 1;
  }

  const firstHeading = lines[index]?.trim().match(REPORT_HEADING_RE);
  if (firstHeading && firstHeading[1].length === 1) {
    index += 1;
    while (index < lines.length && !lines[index].trim()) {
      index += 1;
    }
  }

  const requiredHeading = lines[index]?.trim().match(REPORT_HEADING_RE);
  return (
    requiredHeading?.[1] === '##' &&
    requiredHeading?.[2].trim().toLowerCase() === 'executive summary'
  );
}

/**
 * The citation/online preprocessors and the base markdown component map now
 * live in `./reportMarkdown` so the research report and the documentation
 * surface share one rendering surface (formatting + citation chips) and can't
 * drift. ReportView adds only its grounding-aware `p`/`li` overrides below.
 *
 * BAR-192 Phase C — the small indicator on a flagged block.
 * A compact info button pinned top-right of the amber box (sized to sit on
 * single-line highlights without breaking the layout); hovering it opens a
 * popover with one row per finding + the "not yet checked" framing
 * (focus opens it too, for keyboard users). Renders only span/button so it
 * is valid inside <p>.
 */
function GroundingFlag({
  findings,
  onDecide,
  onFix,
  onFindSource,
  onInternal,
  onValidate,
  validatePending = false,
  submitting = false,
}: {
  findings: GroundingFinding[];
  onDecide?: (d: GroundingReviewDecisionInput) => void;
  onFix?: (f: GroundingFinding) => void;
  onFindSource?: (f: GroundingFinding) => void;
  onInternal?: (f: GroundingFinding) => void;
  onValidate?: () => void;
  validatePending?: boolean;
  submitting?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Hover grace (third-soak rage-fix): closing instantly on mouseleave made
  // the card unreachable — any pixel of dead zone between icon and card
  // killed it mid-travel. Leave starts a short grace timer; re-entering
  // (icon OR card — both are wrapper descendants) cancels it.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openNow = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };
  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 300);
  };
  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  // Phase E: decision identity for in-card accept/reject — present only on
  // adjudicated findings that carry their sentence/marker identity.
  const identityOf = (f: GroundingFinding): GroundingReviewDecisionInput | null => {
    const verdict = f.adjudication?.verdict;
    if (!verdict) return null;
    if (f.kind === 'dangling_reference') {
      return f.marker_id
        ? { kind: f.kind, verdict, decision: 'accepted', marker_id: f.marker_id }
        : null;
    }
    return f.line_sha1
      ? { kind: f.kind, verdict, decision: 'accepted', line_sha1: f.line_sha1 }
      : null;
  };

  const hasConfirmed = findings.some((f) => f.severity === 'confirmed');
  const hasSuspected = findings.some((f) => f.severity === 'suspected');
  // Post-run states: an amber that the judge has seen but could not rule on
  // carries its adjudication; a pre-run amber carries none.
  const hasUnchecked = findings.some(
    (f) => f.severity === 'suspected' && !f.adjudication,
  );
  const hasUnverifiable = findings.some(
    (f) => f.severity === 'suspected' && f.adjudication,
  );

  // The finding whose state the card leads with. It is the one the button
  // row acts on where there is one (so the headline and the buttons always
  // describe the same thing); otherwise the most severe finding on the
  // block, matching the icon's own colour precedence.
  const primary =
    findings.find(
      (f) =>
        identityOf(f) &&
        (f.severity === 'confirmed' ||
          f.severity === 'cleared' ||
          (f.severity === 'suspected' &&
            f.adjudication?.verdict === 'unverifiable' &&
            !f.dismissed)),
    ) ??
    findings.find((f) => f.severity === 'confirmed') ??
    findings.find((f) => f.severity === 'suspected') ??
    findings[0];
  const copy = primary
    ? groundingCardCopy({
        severity: primary.severity,
        verdict: primary.adjudication?.verdict,
        decision: primary.review?.decision,
      })
    : null;

  // The checker's own words (chips, detail, rationale, source quote) sit
  // under a disclosure so the plain-language lines lead. A button rather
  // than <details> because this card renders inside a <p> and may contain
  // only phrasing content — it is the WAI-ARIA disclosure pattern, so it
  // still toggles on Enter/Space and reports its state to a screen reader.
  const detailsId = `grounding-card-details-${primary?.kind ?? 'none'}-${primary?.line ?? 0}`;

  const chip = (cls: string, text: string, struck = false) => (
    <span
      className={`inline-block mr-1.5 px-1 py-px rounded text-[10px] font-semibold uppercase tracking-wide ${cls} ${
        struck ? 'line-through opacity-70' : ''
      }`}
    >
      {text}
    </span>
  );

  return (
    <span
      className="absolute top-1.5 right-1.5 inline-flex"
      // Hover on the ICON opens the card (not the whole block — that felt
      // haunted in preview review). The wrapper contains both icon and
      // card, so moving the cursor from icon into the card keeps it open;
      // leaving either closes it.
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      <button
        type="button"
        data-testid="grounding-flag-button"
        aria-label="Show grounding check details"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className={`h-5 w-5 inline-flex items-center justify-center rounded-md bg-surface border transition-colors ${
          hasConfirmed
            ? 'text-danger border-danger/50 hover:bg-danger-bg'
            : hasSuspected
              ? 'text-warning border-warning/40 hover:bg-warning-bg'
              : 'text-success border-success/40 hover:bg-success-bg'
        }`}
      >
        <Info size={12} strokeWidth={2.25} />
      </button>
      {open && (
        <span
          data-testid="grounding-detail-card"
          className="absolute right-0 top-5 z-20 block w-max max-w-[480px] bg-surface border border-border-default rounded-md shadow-bark-md px-3 py-2 text-left cursor-default"
        >
          {/* Plain language first: what the colour means, then what
              each button does. The checker's own wording waits under
              "Details" below. */}
          {copy ? (
            <span
              data-testid="grounding-card-headline"
              className="block text-xs font-semibold text-fg whitespace-normal"
            >
              {copy.headline}
            </span>
          ) : null}
          {copy?.subline ? (
            <span
              data-testid="grounding-card-subline"
              className="block text-[11px] text-fg-secondary mt-0.5 whitespace-normal"
            >
              {copy.subline}
            </span>
          ) : null}
          {onDecide
            ? findings
                .filter((f) =>
                  identityOf(f) &&
                  (f.severity === 'confirmed' ||
                    f.severity === 'cleared' ||
                    (f.severity === 'suspected' &&
                      f.adjudication?.verdict === 'unverifiable' &&
                      !f.dismissed)),
                )
                .slice(0, 1) // one decidable finding per line in practice; keep the card simple
                .map((f) => {
                  const d = f.review?.decision;
                  // One plain line per button actually on show, saying what
                  // pressing it does. Keyed off the same branch that decides
                  // which buttons render, so a line can never describe an
                  // action this card does not offer.
                  const actionLines: string[] = [];
                  if (d === 'fix_requested') {
                    if (onFix) actionLines.push(GROUNDING_ACTION_LINES.fix);
                  } else if (d === 'source_requested') {
                    if (onFindSource) actionLines.push(GROUNDING_ACTION_LINES.findSource);
                    if (onInternal) actionLines.push(GROUNDING_ACTION_LINES.internal);
                  } else if (d === 'accepted') {
                    if (f.fixable && onFix) actionLines.push(GROUNDING_ACTION_LINES.fix);
                  } else if (d === 'rejected') {
                    // Nothing left to press — the decision is recorded.
                  } else if (f.severity === 'confirmed') {
                    if (onFix) actionLines.push(GROUNDING_ACTION_LINES.fix);
                  } else if (f.severity === 'suspected') {
                    if (onFindSource) actionLines.push(GROUNDING_ACTION_LINES.findSource);
                    if (onInternal) actionLines.push(GROUNDING_ACTION_LINES.internal);
                  } else {
                    actionLines.push(GROUNDING_ACTION_LINES.reject);
                  }
                  return (
                    <span key="decide" className="block mt-1">
                      <span className="flex items-center gap-2 flex-wrap">
                        {d === 'fix_requested' ? (
                          <>
                            <span className="text-[11px] text-fg-tertiary italic">fix in progress</span>
                            {onFix ? (
                              <button type="button" data-testid="card-fix-retry-button" disabled={submitting}
                                onClick={() => onFix(f)}
                                className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50">
                                Fix again
                              </button>
                            ) : null}
                          </>
                        ) : d === 'source_requested' ? (
                          <>
                            <span className="text-[11px] text-fg-tertiary italic">searching for source</span>
                            {onFindSource ? (
                              <button type="button" data-testid="card-find-source-retry-button" disabled={submitting}
                                onClick={() => onFindSource(f)}
                                className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50">
                                Search again
                              </button>
                            ) : null}
                            {onInternal ? (
                              <button type="button" data-testid="card-internal-button" disabled={submitting}
                                onClick={() => onInternal(f)}
                                className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium text-fg-secondary border border-border-default hover:bg-neutral-warm disabled:opacity-50">
                                Internal
                              </button>
                            ) : null}
                          </>
                        ) : d === 'accepted' ? (
                          <>
                            <span
                              data-testid="card-accepted-status"
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-success"
                            >
                              <Check size={16} strokeWidth={1.5} aria-hidden="true" />
                              accepted by you
                            </span>
                            {f.fixable && onFix ? (
                              <button type="button" data-testid="card-fix-button" disabled={submitting}
                                onClick={() => onFix(f)}
                                className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50">
                                Fix
                              </button>
                            ) : null}
                          </>
                        ) : d === 'rejected' ? (
                          <span className="text-[11px] text-fg-tertiary">dismissed by you</span>
                        ) : f.severity === 'confirmed' ? (
                          // Red: no accept/reject gate — a confirmed verdict goes
                          // straight to Fix (the verdict carries the correction).
                          // handleFix records its own fix_requested decision, so
                          // no prior Accept is needed. Confirmed findings always
                          // carry identity (adjudicator.py stamps line_sha1 on
                          // every confirmed item, marker_id for dangling refs), so
                          // they always pass the identityOf() filter and reach
                          // this branch — never a red card with no action.
                          onFix ? (
                            <button type="button" data-testid="card-fix-button" disabled={submitting}
                              onClick={() => onFix(f)}
                              className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50">
                              Fix
                            </button>
                          ) : null
                        ) : f.severity === 'suspected' ? (
                          <>
                            {onFindSource ? (
                              <button type="button" data-testid="card-find-source-button" disabled={submitting}
                                onClick={() => onFindSource(f)}
                                className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50">
                                Find source
                              </button>
                            ) : null}
                            {onInternal ? (
                              <button type="button" data-testid="card-internal-button" disabled={submitting}
                                onClick={() => onInternal(f)}
                                className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium text-fg-secondary border border-border-default hover:bg-neutral-warm disabled:opacity-50">
                                Internal
                              </button>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <button type="button" data-testid="card-accept-button" disabled={submitting}
                              onClick={() => { const id = identityOf(f); if (id) onDecide({ ...id, decision: 'accepted' }); }}
                              className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium bg-success-bg text-success border border-success/40 hover:opacity-90 disabled:opacity-50">
                              Accept
                            </button>
                            <button type="button" data-testid="card-reject-button" disabled={submitting}
                              onClick={() => { const id = identityOf(f); if (id) onDecide({ ...id, decision: 'rejected' }); }}
                              className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium bg-warning-bg text-warning border border-warning/40 hover:opacity-90 disabled:opacity-50">
                              Reject
                            </button>
                          </>
                        )}
                      </span>
                      {actionLines.map((line) => (
                        <span
                          key={line}
                          data-testid="grounding-action-line"
                          className="block mt-0.5 text-[11px] text-fg-secondary whitespace-normal"
                        >
                          {line}
                        </span>
                      ))}
                    </span>
                  );
                })
            : null}
          {hasUnchecked ? (
            <span className="flex flex-wrap items-center gap-2 mt-1">
              <span className="text-[11px] text-fg-tertiary italic whitespace-normal">
                Not yet semantically checked.
              </span>
              {onValidate ? (
                <button type="button" data-testid="card-validate-button" disabled={submitting || validatePending}
                  onClick={onValidate}
                  className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50">
                  {validatePending ? 'Validating…' : 'Validate'}
                </button>
              ) : null}
            </span>
          ) : null}
          <span className="block mt-1.5">
            <button
              type="button"
              data-testid="grounding-card-details-toggle"
              aria-expanded={detailsOpen}
              aria-controls={detailsId}
              onClick={() => setDetailsOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-fg-tertiary hover:text-fg-secondary"
            >
              <ChevronDown
                size={11}
                aria-hidden="true"
                className={detailsOpen ? '' : '-rotate-90'}
              />
              Details
            </button>
          </span>
          <span
            hidden={!detailsOpen}
            id={detailsId}
            data-testid="grounding-card-details"
            // `block` only while open: a Tailwind display utility outranks
            // the user-agent rule for [hidden], so applying it unconditionally
            // would leave the collapsed fold on screen.
            className={`mt-1 ${detailsOpen ? 'block' : ''}`}
          >
            {findings.map((f) => {
              const isSweep = f.kind.startsWith('semantic_');
              const key = `${f.kind}-${f.line}-${f.detail}`;
              if (f.severity === 'cleared') {
                // Was amber, the judge ruled it fine: struck-through flag +
                // the reason it cleared — visibly resolved, not vanished.
                return (
                  <span key={key} className="block text-xs leading-relaxed text-fg-secondary mb-1 whitespace-normal">
                    {chip('bg-warning-bg text-warning', f.kind.replace(/_/g, ' '), true)}
                    {chip('bg-success-bg text-success', 'cleared')}
                    <span className="line-through opacity-60">{f.detail}</span>
                    {f.adjudication?.rationale ? (
                      <span className="block mt-0.5 pl-2 border-l-2 border-success/40 text-fg-tertiary whitespace-normal">
                        {f.adjudication.rationale}
                      </span>
                    ) : null}
                  </span>
                );
              }
              if (f.severity === 'confirmed') {
                return (
                  <span key={key} className="block text-xs leading-relaxed text-fg-secondary mb-1 whitespace-normal">
                    {isSweep
                      ? chip('bg-danger-bg text-danger border border-danger/30', 'new · deep check')
                      : chip('bg-warning-bg text-warning', 'flagged', true)}
                    {chip(
                      'bg-danger-bg text-danger',
                      f.adjudication?.verdict ?? f.kind.replace(/_/g, ' '),
                    )}
                    {f.detail}
                    {!isSweep && f.adjudication?.rationale && f.adjudication.rationale !== f.detail ? (
                      <span className="block mt-0.5 text-fg-secondary whitespace-normal">
                        {f.adjudication.rationale}
                      </span>
                    ) : null}
                    {f.adjudication?.quote ? (
                      <span className="block mt-0.5 pl-2 border-l-2 border-danger/40 text-fg-tertiary italic">
                        source: “{f.adjudication.quote}”
                      </span>
                    ) : null}
                  </span>
                );
              }
              // suspected — pre-run (no adjudication), post-run unverifiable,
              // or a verdict the user rejected (dismissed)
              return (
                <span key={key} className="block text-xs leading-relaxed text-fg-secondary mb-1 whitespace-normal">
                  {chip('bg-warning-bg text-warning', f.kind.replace(/_/g, ' '))}
                  {f.detail}
                  {f.dismissed ? (
                    <span className="block mt-0.5 text-[11px] text-fg-tertiary italic whitespace-normal">
                      verdict dismissed by you — back to flagged
                    </span>
                  ) : f.sourcePending ? (
                    <span className="block mt-0.5 text-[11px] text-fg-tertiary italic whitespace-normal">
                      searching for source — resolves on next validation
                    </span>
                  ) : f.adjudication?.rationale ? (
                    <span className="block mt-0.5 pl-2 border-l-2 border-warning/40 text-fg-tertiary whitespace-normal">
                      could not verify: {f.adjudication.rationale}
                    </span>
                  ) : null}
                </span>
              );
            })}
            {hasUnverifiable ? (
              <span className="block text-[11px] text-fg-tertiary italic mt-1 whitespace-normal">
                Validation checked this but could not verify it against the retrieved sources — it stays flagged.
              </span>
            ) : null}
          </span>
          <span
            data-testid="grounding-card-footer"
            className="block mt-1.5 text-[10px] text-fg-tertiary whitespace-normal"
          >
            {GROUNDING_CARD_FOOTER}
          </span>
        </span>
      )}
    </span>
  );
}

export default function ReportView({
  report,
  draftReport,
  markers,
  findings,
  onCitationClick,
  onlineSupport,
  onOnlineRefClick,
  selectedFindingId,
  selectedOnlineSupportId,
  workspaceId,
  userId,
  authToken,
  onDispatchChatMessage,
  isWelcomeState = false,
}: ReportViewProps) {
  const content = report?.content ?? draftReport;
  const { showToast, dismissToast } = useToast();

  // BAR-60 — local edit state. `localContent` mirrors `content` in
  // read mode and tracks the in-flight edit while the editor is open.
  // After a successful save we keep the saved markdown in `localContent`
  // so the read-mode re-render shows the new text immediately, even
  // before the workspace-state subscription pushes the updated event.
  const [isEditing, setIsEditing] = useState(false);
  const [localContent, setLocalContent] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const editorRef = useRef<{ getMarkdown: () => string } | null>(null);
  const editBaselineRef = useRef<string>('');
  // Tracks the markdown of the last successful save until the parent's
  // `content` prop echoes that exact string back through the workspace-
  // state subscription. Without this guard, the sync useEffect below
  // would clobber the just-saved markdown the instant we exit edit
  // mode (because the parent's `content` still has the pre-save value
  // for the 1–3 second subscription lag). Pattern adapted from the
  // canonical TipTap save-and-resync recipe — see issue #2216 / disc
  // #2871 for the same shape used by Novel, Plate, and Liveblocks.
  const pendingSaveRef = useRef<string | null>(null);

  // Sync localContent with parent-provided content whenever the latter
  // changes (e.g. workspace state refresh, agent regeneration), but
  // hold the line if we're still waiting for a save echo to come back.
  // pendingSaveRef is intentionally omitted from deps because it is a
  // mutable echo guard, not a value that should trigger resync.
  useEffect(() => {
    if (isEditing) return;
    const incoming = content ?? null;
    if (
      pendingSaveRef.current !== null &&
      incoming !== pendingSaveRef.current
    ) {
      // Save is in flight or its echo hasn't arrived yet — keep
      // showing the saved markdown rather than the stale parent value.
      return;
    }
    // Echo arrived (or no save in flight) — accept parent state.
    pendingSaveRef.current = null;
    setLocalContent(incoming);
    // pendingSaveRef is a mutable echo guard; adding it to deps would
    // cause the stale-parent overwrite this effect is preventing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, isEditing]);

  const effectiveContent = localContent ?? content;

  const processedContent = useMemo(() => {
    if (!effectiveContent) return null;
    return preprocessDocumentMarkdown(effectiveContent);
  }, [effectiveContent]);

  // The shared base renderer (citation chips, tables, headings, lists, …).
  // ReportView overrides only `p`/`li` with grounding-aware variants below;
  // everything else is identical to the documentation surface.
  const baseComponents = useMemo(
    () =>
      buildBaseMarkdownComponents({
        markers,
        findings,
        onlineSupport,
        onCitationClick,
        onOnlineRefClick,
        selectedFindingId,
        selectedOnlineSupportId,
      }),
    [
      markers,
      findings,
      onlineSupport,
      onCitationClick,
      onOnlineRefClick,
      selectedFindingId,
      selectedOnlineSupportId,
    ],
  );

  const canEdit = Boolean(report?.report_id && workspaceId);

  // ── BAR-192 Phase C: grounding seal → badge + suspected highlights ──
  // The seal arrives with workspace /state (Phase B emits it on every
  // grounding run; the reducer flips `stale` when the report mutates).
  // Line anchors are 1-indexed raw-markdown lines and survive the citation
  // preprocessors above (all inline replaces — line counts unchanged).
  const dictWorkspace = useProjectStore((s) => s.dictWorkspace);
  const groundingSeal =
    dictWorkspace && (!workspaceId || dictWorkspace.workspace_id === workspaceId)
      ? dictWorkspace.report_grounding_seal ?? null
      : null;
  // BAR-192 auto-repair: the latest in-call auto-repair run summary (legible
  // trail R5) — null until validate_work has auto-repaired this workspace.
  const autoRepair =
    dictWorkspace && (!workspaceId || dictWorkspace.workspace_id === workspaceId)
      ? dictWorkspace.auto_repair ?? null
      : null;
  // The latest validate_work review. Shown under the badge regardless of
  // what the grounding seal says, so a user-requested review stays visible
  // after the automatic report check has run again.
  const workReview =
    dictWorkspace && (!workspaceId || dictWorkspace.workspace_id === workspaceId)
      ? dictWorkspace.report_review ?? null
      : null;
  // validate_work's verdict per node is approved / flagged / rejected. A run
  // PASSES when nothing is rejected (flagged is advisory, #805); only a
  // full-graph, completed run is authoritative. That is the rule the plan
  // flow already ships on, so the badge says the same thing.
  const reviewCounts = useMemo(
    () => workReview?.counts ?? { approved: 0, flagged: 0, rejected: 0 },
    [workReview],
  );
  const reviewVerdict = String(workReview?.overall_verdict || '');
  const reviewFullGraph = !workReview?.scope || workReview.scope === 'full_graph';
  // Fail closed: only a session that says it completed counts as complete.
  const reviewComplete =
    !!workReview && workReview.validation_execution_complete === true && reviewVerdict !== 'abandoned';
  const reviewRejected = !!workReview && (reviewVerdict === 'rejected' || reviewCounts.rejected > 0);
  const reviewPassed =
    !!workReview && !reviewRejected && reviewComplete && reviewFullGraph
    && (reviewVerdict === 'approved' || reviewVerdict === 'partial');
  const workReviewLine = useMemo(() => {
    if (!workReview) return null;
    const c = reviewCounts;
    const cls = reviewRejected ? 'text-danger' : reviewPassed ? 'text-success' : 'text-warning';
    const when = workReview.completed_at ? ` (${workReview.completed_at})` : '';
    return {
      cls: workReview.stale ? 'text-fg-tertiary' : cls,
      text:
        `validate_work: ${c.rejected} rejected, ${c.flagged} flagged, ${c.approved} approved` +
        (workReview.stale ? ' · edited since validation' : ''),
      tip: workReview.stale
        ? `validate_work ran${when}; the report or graph changed since. Ask for validation again to re-check.${workReview.summary ? ` ${workReview.summary}` : ''}`
        : `validate_work ran${when}.${workReview.summary ? ` ${workReview.summary}` : ''}`,
    };
  }, [workReview, reviewCounts, reviewRejected, reviewPassed]);
  // The flags themselves: node name + the reviewer's reason, from the node
  // reviews of the session the badge reports. "8 flagged" must open into a
  // list a reader can act on, not stay a number.
  const [showReviewFlags, setShowReviewFlags] = useState(false);
  useEffect(() => {
    setShowReviewFlags(false);
  }, [workReview?.session_id, workspaceId]);
  const reviewFlags = useMemo(() => {
    if (!workReview || !dictWorkspace?.nodes) return [];
    const out: { key: string; nodeId: string; name: string; verdict: string; rationale: string }[] = [];
    for (const [nid, node] of Object.entries(dictWorkspace.nodes)) {
      for (const rv of node.reviews ?? []) {
        if (rv.session_id !== workReview.session_id) continue;
        const verdict = String(rv.verdict ?? '');
        if (verdict !== 'flagged' && verdict !== 'rejected') continue;
        out.push({
          key: String(rv.review_id ?? `${nid}-${verdict}`),
          nodeId: nid,
          name: String(node.label || nid),
          verdict,
          rationale: String(rv.rationale ?? ''),
        });
      }
    }
    return out.sort((a, b) => Number(b.verdict === 'rejected') - Number(a.verdict === 'rejected'));
  }, [workReview, dictWorkspace]);

  // ── BAR-192 Phase E: review decisions (accept/reject + Fix) ──
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  // Idempotency guard for the pre-run amber Validate button: set on click,
  // re-enabled when a fresh seal lands (validation ran → `ran` flips, or the
  // report changed → content_hash changes). Stops double-clicks firing
  // duplicate validate_work chat dispatches.
  const [validatePending, setValidatePending] = useState(false);
  useEffect(() => {
    setValidatePending(false);
  }, [groundingSeal?.content_hash, groundingSeal?.adjudication?.ran]);
  const reviewProgress = useMemo(
    () => groundingReviewProgress(groundingSeal),
    [groundingSeal],
  );
  // Phase F: the raw decision map (rides /state) + derived repair outcomes —
  // a repaired finding is GONE from the seal, only the map remembers it.
  const groundingReviews = dictWorkspace?.grounding_reviews;
  const repairedRows = useMemo(
    () => groundingRepairedRows(groundingSeal, groundingReviews),
    [groundingSeal, groundingReviews],
  );
  const reviewable = Boolean(
    groundingSeal && !groundingSeal.stale && groundingSeal.adjudication?.ran &&
    workspaceId && userId,
  );

  const handleDecisions = useCallback(
    async (decisions: GroundingReviewDecisionInput[]): Promise<boolean> => {
      if (!workspaceId || !userId || decisions.length === 0) return false;
      setReviewSubmitting(true);
      setReviewError(null);
      const result = await submitGroundingReview(
        workspaceId,
        userId,
        decisions,
        dictWorkspace?.compartment_id || undefined,
      );
      if (result.success) {
        // The reducer owns the merge — refetch so the decided state is the
        // SERVER's view, not an optimistic guess.
        const projectId = useProjectStore.getState().activeProject?.project_id;
        if (projectId) {
          // fresh: this refetch exists to read the SERVER's post-decision view,
          // so it must not join a request issued before the write (BAR-248).
          await useProjectStore.getState().loadWorkspaceState(projectId, userId, { fresh: true });
        }
      } else {
        setReviewError(result.error || 'Could not save the decision — try again.');
      }
      setReviewSubmitting(false);
      return result.success;
    },
    [workspaceId, userId, dictWorkspace?.compartment_id],
  );

  // Phase F: one shape for every repair target — drawer rows pass their
  // identity, card findings carry the same fields directly.
  type RepairTarget = {
    kind: string;
    verdict?: string;
    line_sha1?: string;
    marker_id?: string;
    line?: number;
    detail?: string;
    adjudication?: { verdict?: string; rationale?: string; quote?: string };
  };

  const identityFor = (
    t: RepairTarget,
    decision: GroundingReviewDecisionInput['decision'],
  ): GroundingReviewDecisionInput | null => {
    const verdict = t.verdict ?? t.adjudication?.verdict;
    if (!verdict) return null;
    if (t.kind === 'dangling_reference') {
      return t.marker_id ? { kind: t.kind, verdict, decision, marker_id: t.marker_id } : null;
    }
    return t.line_sha1 ? { kind: t.kind, verdict, decision, line_sha1: t.line_sha1 } : null;
  };

  // The verbatim report line a finding sits on — the ONLY reliable anchor
  // for the agent's edit. Line number + rationale alone let it drift to a
  // look-alike sentence (REQ-069a soak: agent fixed a different initial-
  // margin line and the flagged one stayed red forever).
  const flaggedSentence = (line?: number): string => {
    if (!line || !effectiveContent) return '';
    return (effectiveContent.split('\n')[line - 1] ?? '').trim();
  };

  const fixInstruction = (t: RepairTarget): string => {
    const verdict = t.adjudication?.verdict ?? 'confirmed issue';
    const why = t.adjudication?.rationale || t.detail || '';
    const quote = t.adjudication?.quote
      ? ` The source says: "${t.adjudication.quote}".`
      : '';
    const sentence = flaggedSentence(t.line);
    const head = sentence
      ? `Fix exactly this sentence${t.line ? ` (line ${t.line})` : ''} and NO OTHER, even if another sentence looks similar:\n"${sentence}"\n\nThe issue — ${verdict}: ${why}.${quote}`
      : `Fix the confirmed issue${t.line ? ` on line ${t.line}` : ''} of the report — ${verdict}: ${why}.${quote}`;
    return (
      `${head} Make a minimal edit to that exact sentence only; keep ` +
      `the citation unless it points at the wrong source. After the edit, run ` +
      `check_report_grounding — the fix is NOT complete and your turn must not end until that check has run.`
    );
  };

  // Submit-then-dispatch: the in-flight state must be on record BEFORE the
  // agent starts; a failed submit dispatches nothing.
  const handleRepair = useCallback(
    async (
      t: RepairTarget,
      decision: 'fix_requested' | 'source_requested',
      instruction: string,
    ) => {
      const id = identityFor(t, decision);
      if (!id || !onDispatchChatMessage) return;
      const ok = await handleDecisions([id]);
      if (ok) {
        // Structured repair flag → the backend done-gate forces real tool
        // calls + verify before the turn can end, regardless of wording.
        onDispatchChatMessage(instruction, { repair: true });
        setReviewOpen(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleDecisions, onDispatchChatMessage],
  );

  const handleFindSource = useCallback(
    (t: RepairTarget) => {
      const claim = flaggedSentence(t.line) || t.detail || '';
      const why = t.adjudication?.rationale || 'the cited source did not contain the needed detail';
      return handleRepair(
        t,
        'source_requested',
        `Find the missing source for exactly this claim${t.line ? ` (line ${t.line})` : ''} and NO OTHER, even if another looks similar:\n` +
          `"${claim}"\n\nThe validation judge could not verify it: ${why}. Retrieve the ` +
          `specific provision that covers this, add it as a finding, then run ` +
          `check_report_grounding — the retrieval is NOT complete and your turn must not end until that check has run.`,
      );
    },
    [handleRepair],
  );

  const handleBulkFix = useCallback(
    async (rows: ReviewRow[]) => {
      if (!onDispatchChatMessage) return;
      const ids = rows.reduce<GroundingReviewDecisionInput[]>((acc, r) => {
        if (r.identity) acc.push({ ...r.identity, decision: 'fix_requested' });
        return acc;
      }, []);
      if (ids.length === 0) return;
      const ok = await handleDecisions(ids);
      if (!ok) return;
      const items = rows
        .map((r, i) => {
          const sentence = flaggedSentence(r.line) || r.detail || '';
          return (
            `${i + 1}. Edit exactly this sentence${r.line ? ` (line ${r.line})` : ''} and no other, even if another looks similar:\n` +
            `   "${sentence}"\n` +
            `   Issue — ${r.adjudication?.verdict ?? 'confirmed'}: ${r.adjudication?.rationale || r.detail}` +
            (r.adjudication?.quote ? ` (source says: "${r.adjudication.quote}")` : '')
          );
        })
        .join('\n');
      onDispatchChatMessage(
        `Fix the following ${rows.length} confirmed issues in the report. Edit ONLY the ` +
          `exact sentences quoted below — never a similar-looking one — and keep citations ` +
          `unless they point at the wrong source. Do not delete claims just to silence the check.\n${items}\n` +
          `After the edits, run check_report_grounding — the fixes are NOT complete and your turn must not end until that check has run.`,
        { repair: true },
      );
      setReviewOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleDecisions, onDispatchChatMessage],
  );

  const handleInternal = useCallback(
    (t: RepairTarget) => {
      const id = identityFor(t, 'accepted_internal');
      if (id) void handleDecisions([id]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleDecisions],
  );

  // Fix (Phase F's entry point): a pre-filled chat instruction — one click,
  // but always the USER's click. Hidden when the parent didn't wire chat.
  const handleFix = useCallback(
    (t: RepairTarget) => handleRepair(t, 'fix_requested', fixInstruction(t)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleRepair],
  );

  // Validate (the pre-run amber entry point): dispatch a chat instruction to
  // run validate_work(full). There is no HTTP endpoint for validate_work — the
  // agent must call the tool — so, like Fix, this is a pre-filled prompt the
  // user fires with one click. Whole-report: one click adjudicates every amber.
  const handleValidate = useCallback(() => {
    // Idempotency: validate_work is whole-report and a pre-run amber carries no
    // verdict/identity to key a persisted decision on, so (unlike Fix's submit-
    // then-dispatch) we guard on validatePending to stop double-clicks firing
    // duplicate dispatches; the effect above re-enables on the next seal.
    if (!onDispatchChatMessage || validatePending) return;
    onDispatchChatMessage(
      'Validate the current report against its findings now: run validate_work with ' +
        'mode="full". This re-judges every regulatory claim\'s grounding and surfaces any ' +
        'confirmed or unverifiable issues. Your turn must not end until that validation has run.',
    );
    setValidatePending(true);
  }, [onDispatchChatMessage, validatePending]);

  // line → findings on that line. Empty for missing/stale seals (a stale
  // seal's anchors refer to a previous report version).
  const groundingByLine = useMemo(() => {
    const m = new Map<number, GroundingFinding[]>();
    // effectiveContent: dangling refs are itemised without line numbers in
    // the seal — the normalizer locates their {ref:id} tokens in the raw
    // markdown so they highlight like every other finding.
    for (const f of normalizeGroundingFindings(groundingSeal, effectiveContent)) {
      const list = m.get(f.line) ?? [];
      list.push(f);
      m.set(f.line, list);
    }
    return m;
  }, [groundingSeal, effectiveContent]);

  const groundingForNode = (
    node?: { position?: { start?: { line?: number } } },
  ): GroundingFinding[] | null => {
    const line = node?.position?.start?.line;
    if (typeof line !== 'number') return null;
    return groundingByLine.get(line) ?? null;
  };

  // Phase C renders only the 'suspected' tier (soft amber, warning token).
  // Phase D adds 'confirmed' (danger tier) / 'cleared' — the switch is here
  // so D extends this map instead of reworking the renderers.
  const GROUNDING_HL: Record<string, string> = {
    suspected: 'bg-warning-bg border border-warning/30 rounded-md',
    // Phase D: adjudicator-confirmed issues get the danger tier — the
    // urgent half of the locked two-tier design.
    confirmed: 'bg-danger-bg border border-danger/40 rounded-md',
    // Post-run state: a cleared flag stays visible (success tint, struck
    // detail in the card) so the user SEES what validation resolved
    // instead of flags silently vanishing.
    cleared: 'bg-success-bg border border-success/30 rounded-md',
  };
  const groundingClass = (found: GroundingFinding[] | null): string => {
    if (!found || found.length === 0) return '';
    const severity = found.some((f) => f.severity === 'confirmed')
      ? 'confirmed'
      : found.some((f) => f.severity === 'suspected')
        ? 'suspected'
        : found[0].severity;
    return GROUNDING_HL[severity] ?? '';
  };
  // Badge: the three aggregate states from the master-plan design.
  // stale wins over everything — a changed report is simply "not validated".
  const sealBadge = useMemo((): {
    cls: string; label: string; tip: string; sub?: string; icon?: LucideIcon;
  } => {
    if (!groundingSeal || groundingSeal.stale) {
      return {
        cls: 'bg-bg-warm text-fg-secondary',
        label: 'Not validated',
        tip: 'This version of the report has not been checked against its findings yet. The check runs automatically when the report changes.',
      };
    }
    // Second-soak feedback: when the judge HAS run, say so explicitly
    // under the badge — "✗ N confirmed" alone didn't communicate that
    // validation already happened. Carried = verdicts inherited across an
    // edit for sentences that didn't change; the counter says how far the
    // report has drifted from the validated version (third-soak feedback).
    const editsSince = groundingSeal.adjudication?.edits_since_validation ?? 0;
    const sub = groundingSeal.adjudication?.ran
      ? groundingSeal.adjudication.carried
        ? `verdicts carried from last validation${
            editsSince > 0
              ? ` · ${formatCount(editsSince, { one: 'edit', other: 'edits' })} since`
              : ''
          }`
        : 'semantic validation ran on this version'
      : undefined;
    // Phase D precedence: confirmed (adjudicator-verified issues, danger)
    // beats suspected (deterministic flags, amber) beats validated.
    // Phase E: counts AFTER review decisions (a rejected verdict is amber
    // again); review progress rides the label so "has a human looked?" is
    // visible at the badge.
    const effective = groundingEffectiveCounts(groundingSeal);
    const progress = groundingReviewProgress(groundingSeal);
    const confirmed = effective.confirmed;
    if (confirmed > 0) {
      const reviewSuffix = progress.awaiting > 0
        ? ` · ${progress.awaiting} awaiting review`
        : progress.reviewed > 0
          ? ' · reviewed'
          : '';
      return {
        cls: 'bg-danger-bg text-danger',
        icon: XCircle,
        label: `${formatCount(confirmed, { one: 'confirmed issue', other: 'confirmed issues' })}${reviewSuffix}`,
        tip: groundingSeal.adjudication?.carried
          ? 'These verdicts were carried over from your last validation — only sentences that have not changed since keep them. Edited sentences reset to amber.'
          : 'Validation confirmed these issues against the cited sources — see the red sections for the verdict and the source quote.',
        sub,
      };
    }
    const suspected = effective.suspected;
    if (suspected > 0 || (!groundingSeal.adjudication?.ran && groundingSeal.verdict !== 'approved')) {
      // Post-run framing differs: these ambers HAVE been checked — the
      // judge just couldn't rule either way (honest middle state).
      return groundingSeal.adjudication?.ran
        ? {
            cls: 'bg-warning-bg text-warning',
            icon: AlertTriangle,
            label: `${suspected} flagged · unverified`,
            tip: 'Validation checked these but could not verify them against the retrieved sources — hover the amber sections for why.',
            sub,
          }
        : {
            cls: 'bg-warning-bg text-warning',
            icon: AlertTriangle,
            label: `${suspected} flagged · unvalidated`,
            tip: 'Deterministic checks flagged items in this report (hover the amber sections for details). Not yet semantically checked — running validation will check them.',
          };
    }
    const cleared = groundingSeal.adjudication?.ran
      ? groundingSeal.adjudication.cleared_count ?? 0
      : 0;
    const clearedNote = cleared > 0
      ? `${cleared} flag${cleared === 1 ? ' was' : 's were'} checked and cleared (struck-through in the report). `
      : '';
    const checkedAt = groundingSeal.checked_at ? ` (checked ${groundingSeal.checked_at})` : '';
    const groundingSub = groundingSeal.validated_by === 'check_report_grounding'
      ? `grounding check ran · ${groundingSeal.report_changed ? 'changes were made to the report' : 'no changes needed'}`
      : sub;
    const editedSince = workReview?.stale ? ' · edited since validation' : '';
    const c = reviewCounts;
    if (workReview && reviewRejected) {
      return {
        cls: 'bg-danger-bg text-danger',
        icon: XCircle,
        label: `validate_work: rejected${c.rejected > 0 ? ` · ${c.rejected} rejected, ${c.flagged} flagged` : ''}`,
        tip: `validate_work rejected ${formatCount(c.rejected, { one: 'node', other: 'nodes' })}; a rejected node must be fixed. ${workReview.summary ?? ''}`,
        sub: `${groundingSub ?? ''}${editedSince}`.replace(/^ · /, ''),
      };
    }
    if (workReview && !reviewPassed) {
      return {
        cls: 'bg-warning-bg text-warning',
        icon: AlertTriangle,
        label: !reviewComplete
          ? 'validate_work: did not complete'
          : `validate_work: partial review · ${c.flagged} flagged`,
        tip: !reviewComplete
          ? `The review did not complete; ask for validation again. ${workReview.summary ?? ''}`
          : `Only some nodes were re-reviewed (incremental). A full validate_work is needed to count as validated. ${workReview.summary ?? ''}`,
        sub: `${groundingSub ?? ''}${editedSince}`.replace(/^ · /, ''),
      };
    }
    if (workReview && reviewPassed && workReview.stale) {
      // Stale always wins over green: the passing run covered an earlier
      // version of the report or graph, so it is stated as such, not as
      // "Validated" for the text now on screen.
      return {
        cls: 'bg-bg-warm text-fg-secondary',
        icon: CheckCircle2,
        label: c.flagged > 0 ? `Validated before edits · ${c.flagged} flagged` : 'Validated before edits',
        tip: `validate_work passed an earlier version${checkedAt}; the report or graph changed since. Ask for validation again to re-check.`,
        sub: groundingSub,
      };
    }
    if (workReview && reviewPassed) {
      return {
        cls: 'bg-success-bg text-success',
        icon: CheckCircle2,
        label: c.flagged > 0 ? `Validated · ${c.flagged} flagged` : 'Validated',
        tip: `${clearedNote}validate_work passed: nothing rejected${
          c.flagged > 0 ? `, ${c.flagged} flagged (listed below)` : ''
        }${checkedAt}.`,
        sub: groundingSub,
      };
    }
    // No validate_work on record: the automatic report check says exactly
    // what it did: it ran, and whether it changed the report.
    return {
      cls: 'bg-success-bg text-success',
      icon: CheckCircle2,
      label: 'Grounding check ran',
      tip: `${clearedNote}The report was checked against its findings${checkedAt}${
        groundingSeal.report_changed ? ' and confirmed errors were corrected in place' : ''
      }. This is not the broader validate_work review; ask for validation to run it.`,
      sub: groundingSeal.report_changed ? 'changes were made to the report' : 'no changes needed',
    };
  }, [groundingSeal, workReview, reviewCounts, reviewRejected, reviewPassed, reviewComplete]);
  const SealBadgeIcon = sealBadge.icon;

  const handleStartEdit = () => {
    const currentMarkdown = localContent ?? content ?? '';
    setSaveError(null);
    setLocalContent(currentMarkdown);
    editBaselineRef.current = currentMarkdown;
    setHasUnsavedChanges(false);
    // Keep the visible markdown as the edit baseline. Do not blindly
    // reset from `content` here — while a save echo is pending, the
    // parent prop can still be the stale pre-save value.
    setIsEditing(true);
  };

  const handleEditorChange = useCallback((markdown: string) => {
    setLocalContent(markdown);
    setHasUnsavedChanges(markdown !== editBaselineRef.current);
    if (saveError) setSaveError(null);
  }, [saveError]);

  const discardEdits = () => {
    setIsEditing(false);
    setSaveError(null);
    setHasUnsavedChanges(false);
    editBaselineRef.current = '';
    // Cancel reverts to the parent's authoritative content. If a save
    // is mid-flight (pendingSaveRef !== null), accept the parent
    // value too — the user explicitly chose to discard whatever was
    // in the editor.
    pendingSaveRef.current = null;
    setLocalContent(content ?? null);
    setDiscardDialogOpen(false);
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setDiscardDialogOpen(true);
      return;
    }
    discardEdits();
  };

  useEffect(() => {
    if (!isEditing || !hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEditing, hasUnsavedChanges]);

  const handleSave = async () => {
    if (!report?.report_id || !workspaceId || isSaving) return;
    let markdown: string;
    try {
      markdown = editorRef.current?.getMarkdown() ?? localContent ?? '';
    } catch {
      setSaveError('Could not read the editor content. Reload the page and try again.');
      return;
    }
    if (!hasExecutiveSummaryAtTop(markdown)) {
      setSaveError(REPORT_EXECUTIVE_SUMMARY_REQUIRED_MESSAGE);
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    // Forward the existing title to the backend on every save so the
    // endpoint stays stateless — if we omitted it the backend would
    // need to round-trip to read the existing report just to preserve
    // the title across the new event payload.
    const result = await updateWorkspaceReportContent(
      workspaceId,
      report.report_id,
      markdown,
      report.title ?? '',
      authToken,
    );
    setIsSaving(false);
    if (!result.success) {
      setSaveError(result.error || 'Failed to save report');
      return;
    }
    // Mark the saved markdown as pending-echo. The sync useEffect will
    // hold off on syncing from `content` until the parent's value
    // actually matches this string — at which point we know the
    // server-side state derivation has caught up and it's safe to
    // accept whatever the parent gives us.
    pendingSaveRef.current = markdown;
    setLocalContent(markdown);
    editBaselineRef.current = markdown;
    setHasUnsavedChanges(false);
    setIsEditing(false);
  };

  // BAR-34 export state — each format tracks its own loading + error state so
  // retries stay tied to the format that failed. Gated on the presence of a
  // stored report (not a draft) because the export endpoints read from the
  // workspace state's `reports` dict, which only contains finalized reports.
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pdfExportError, setPdfExportError] = useState<string | null>(null);
  const [isExportingDocx, setIsExportingDocx] = useState(false);
  const [docxExportError, setDocxExportError] = useState<string | null>(null);
  const [isExportingPptx, setIsExportingPptx] = useState(false);
  const [pptxExportError, setPptxExportError] = useState<string | null>(null);
  const [isExportingGdocs, setIsExportingGdocs] = useState(false);
  const [gdocsExportError, setGdocsExportError] = useState<string | null>(null);
  const canExport = Boolean(report?.report_id && workspaceId && userId);
  const isAnyExporting = isExportingPdf || isExportingDocx || isExportingPptx || isExportingGdocs;
  const lastExportError = pdfExportError || docxExportError || pptxExportError || gdocsExportError;
  const exportTargetKey = `${workspaceId ?? ''}:${report?.report_id ?? ''}:${userId ?? ''}`;
  const nextExportRequestIdRef = useRef(0);
  const inFlightExportRequestsRef = useRef<Partial<Record<ExportFormat, number>>>({});
  const exportToastIdsRef = useRef<Partial<Record<ExportFormat, string>>>({});

  const beginExport = (format: ExportFormat): number | null => {
    if (inFlightExportRequestsRef.current[format] !== undefined) return null;
    nextExportRequestIdRef.current += 1;
    inFlightExportRequestsRef.current[format] = nextExportRequestIdRef.current;
    return nextExportRequestIdRef.current;
  };
  const isCurrentExport = (format: ExportFormat, requestId: number): boolean => (
    inFlightExportRequestsRef.current[format] === requestId
  );
  const finishExport = (format: ExportFormat, requestId: number): boolean => {
    if (!isCurrentExport(format, requestId)) return false;
    delete inFlightExportRequestsRef.current[format];
    return true;
  };
  const dismissExportFailure = (format: ExportFormat) => {
    const toastId = exportToastIdsRef.current[format];
    if (!toastId) return;
    dismissToast(toastId);
    delete exportToastIdsRef.current[format];
  };
  const showExportFailure = (
    format: ExportFormat,
    message: string,
    retry: () => Promise<void>,
  ) => {
    dismissExportFailure(format);
    exportToastIdsRef.current[format] = showToast({
      variant: 'error',
      message: `${format} wasn’t downloaded. ${message}`,
      action: {
        label: `Retry ${format}`,
        pendingLabel: `Retrying ${format}…`,
        onClick: retry,
      },
    });
  };

  useEffect(() => {
    const resetExportFeedback = () => {
      Object.values(exportToastIdsRef.current).forEach((toastId) => {
        if (toastId) dismissToast(toastId);
      });
      exportToastIdsRef.current = {};
      inFlightExportRequestsRef.current = {};
    };

    setIsExportingPdf(false);
    setIsExportingDocx(false);
    setIsExportingPptx(false);
    setIsExportingGdocs(false);
    setPdfExportError(null);
    setDocxExportError(null);
    setPptxExportError(null);
    setGdocsExportError(null);

    return resetExportFeedback;
  }, [dismissToast, exportTargetKey]);

  // Export-format dropdown — single trigger with four menu items
  // (PDF / Word / PowerPoint / Google Docs). Closed by click-outside or Escape.
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isExportMenuOpen) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setIsExportMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsExportMenuOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExportMenuOpen]);

  const handleExportPdf = async (): Promise<void> => {
    if (!report?.report_id || !workspaceId || !userId) return;
    const requestId = beginExport('PDF');
    if (!requestId) return;
    setIsExportingPdf(true);
    try {
      const result = await exportWorkspaceReportPdf(workspaceId, report.report_id, userId, authToken);
      if (!isCurrentExport('PDF', requestId)) return;
      if (result.success) {
        setPdfExportError(null);
        dismissExportFailure('PDF');
      } else {
        const message = result.error || 'Failed to export PDF';
        setPdfExportError(message);
        showExportFailure('PDF', message, handleExportPdf);
      }
    } catch {
      if (!isCurrentExport('PDF', requestId)) return;
      const message = 'Failed to export PDF';
      setPdfExportError(message);
      showExportFailure('PDF', message, handleExportPdf);
    } finally {
      if (finishExport('PDF', requestId)) setIsExportingPdf(false);
    }
  };

  const handleExportDocx = async (): Promise<void> => {
    if (!report?.report_id || !workspaceId || !userId) return;
    const requestId = beginExport('Word');
    if (!requestId) return;
    setIsExportingDocx(true);
    try {
      const result = await exportWorkspaceReportDocx(workspaceId, report.report_id, userId, authToken);
      if (!isCurrentExport('Word', requestId)) return;
      if (result.success) {
        setDocxExportError(null);
        dismissExportFailure('Word');
      } else {
        const message = result.error || 'Failed to export Word document';
        setDocxExportError(message);
        showExportFailure('Word', message, handleExportDocx);
      }
    } catch {
      if (!isCurrentExport('Word', requestId)) return;
      const message = 'Failed to export Word document';
      setDocxExportError(message);
      showExportFailure('Word', message, handleExportDocx);
    } finally {
      if (finishExport('Word', requestId)) setIsExportingDocx(false);
    }
  };

  const handleExportPptx = async (): Promise<void> => {
    if (!report?.report_id || !workspaceId || !userId) return;
    const requestId = beginExport('PowerPoint');
    if (!requestId) return;
    setIsExportingPptx(true);
    try {
      const result = await exportWorkspaceReportPptx(workspaceId, report.report_id, userId, authToken);
      if (!isCurrentExport('PowerPoint', requestId)) return;
      if (result.success) {
        setPptxExportError(null);
        dismissExportFailure('PowerPoint');
      } else {
        const message = result.error || 'Failed to export PowerPoint';
        setPptxExportError(message);
        showExportFailure('PowerPoint', message, handleExportPptx);
      }
    } catch {
      if (!isCurrentExport('PowerPoint', requestId)) return;
      const message = 'Failed to export PowerPoint';
      setPptxExportError(message);
      showExportFailure('PowerPoint', message, handleExportPptx);
    } finally {
      if (finishExport('PowerPoint', requestId)) setIsExportingPptx(false);
    }
  };

  // BAR-34 v3 (cheap path) — "Open in Google Docs" downloads the .docx
  // using the existing export endpoint AND opens Google Docs in a new
  // tab. Drive auto-converts on upload, so the user drops the file in
  // and gets a live Google Doc. Native Drive API integration (a real
  // Google OAuth flow + drive.file scope verification) is its own
  // ticket. The window.open() must run synchronously inside the click
  // handler — pop-up blockers reject opens that happen after an await.
  const handleOpenInGoogleDocs = async (): Promise<void> => {
    if (!report?.report_id || !workspaceId || !userId) return;
    const requestId = beginExport('Google Docs');
    if (!requestId) return;
    const gdocsTab = window.open('https://docs.google.com/document/u/0/', '_blank', 'noopener,noreferrer');
    setIsExportingGdocs(true);
    try {
      const result = await exportWorkspaceReportDocx(workspaceId, report.report_id, userId, authToken);
      if (!isCurrentExport('Google Docs', requestId)) {
        gdocsTab?.close();
        return;
      }
      if (result.success) {
        setGdocsExportError(null);
        dismissExportFailure('Google Docs');
      } else {
        const message = result.error || 'Failed to download document';
        setGdocsExportError(message);
        showExportFailure('Google Docs', message, handleOpenInGoogleDocs);
        gdocsTab?.close();
      }
    } catch {
      if (!isCurrentExport('Google Docs', requestId)) {
        gdocsTab?.close();
        return;
      }
      const message = 'Failed to download document';
      setGdocsExportError(message);
      showExportFailure('Google Docs', message, handleOpenInGoogleDocs);
      gdocsTab?.close();
    } finally {
      if (finishExport('Google Docs', requestId)) setIsExportingGdocs(false);
    }
  };

  if (!processedContent) {
    // Welcome panel shows ONLY while the project is truly fresh —
    // no report, no graph nodes, no findings (computed by the parent
    // and passed in as `isWelcomeState`). Once research starts producing
    // any artifact, fall through to the in-flight placeholder so the
    // tutorial doesn't hang around during an active research run.
    if (isWelcomeState) {
      return <ResearchWelcomePanel />;
    }
    return (
      <div className="h-full w-full flex justify-center overflow-hidden pt-14 pb-6 px-6">
        <div className="w-full max-w-[820px] h-full bg-surface border border-border-default shadow-bark-xs rounded-xl flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <Loader2 className="w-10 h-10 mx-auto mb-4 text-fg-tertiary animate-spin" strokeWidth={1.5} />
            <h3 className="text-lg font-medium text-fg-secondary mb-2">Building your report</h3>
            <p className="text-sm text-fg-tertiary">
              Findings are coming in. Switch to the Graph tab to watch evidence land in real time.
            </p>
          </div>
        </div>
      </div>
    );
  }


  return (
    // Outer frame: sits inside the grey workspace shell, centers a framed card
    // and reserves space for the floating workspace tab pill above.
    // `relative` anchors the Phase E review drawer (absolute right overlay).
    <div className="h-full w-full flex justify-center overflow-hidden pt-14 pb-6 px-6 relative">
      {reviewOpen && groundingSeal && reviewable ? (
        <GroundingReviewDrawer
          seal={groundingSeal}
          repaired={repairedRows}
          submitting={reviewSubmitting}
          error={reviewError}
          onClose={() => setReviewOpen(false)}
          onDecide={(d) => void handleDecisions(d)}
          onFix={onDispatchChatMessage ? (row) => void handleFix({ ...(row.identity ?? { kind: row.kind }), line: row.line, detail: row.detail, adjudication: row.adjudication }) : undefined}
          onFindSource={onDispatchChatMessage ? (row) => void handleFindSource({ ...(row.identity ?? { kind: row.kind }), line: row.line, detail: row.detail, adjudication: row.adjudication }) : undefined}
          onInternal={(row) => row.identity && handleInternal({ ...row.identity, line: row.line })}
          onBulkFix={onDispatchChatMessage ? (rows) => void handleBulkFix(rows) : undefined}
        />
      ) : null}
      {/* Report card: white interior framed against the grey shell.
          Scrolling happens inside this card, not on the shell. */}
      <div className="w-full max-w-[820px] h-full bg-surface border border-border-default shadow-bark-xs rounded-xl overflow-y-auto">
        <div className="px-8 py-6">
          {/* BAR-34 — minimal toolbar pinned top-right of the report card.
              BAR-60 — adds an "Edit" button beside the export menu and
              swaps in Save/Cancel controls while the editor is open.
              Only renders when the parent has wired workspace + user
              context (i.e. the report exists in workspace state and is
              eligible for server-side mutation). */}
          {(canExport || canEdit) && (
            <div className="flex justify-end items-center gap-2 mb-3">
              {isEditing ? (
                <>
                  {saveError && (
                    <span className="text-xs text-danger mr-1" role="alert">
                      {saveError}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    aria-label="Cancel edits"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold bg-surface text-fg border border-border-default hover:bg-bg-warm hover:border-border-strong transition-colors disabled:opacity-60"
                  >
                    <X className="w-3.5 h-3.5" aria-hidden="true" />
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    aria-label="Save report"
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold transition-colors ${
                      isSaving
                        ? 'bg-surface text-fg-tertiary border border-border-default cursor-wait'
                        : 'bg-primary text-on-primary border border-primary hover:bg-primary-hover'
                    }`}
                  >
                    {isSaving ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                    ) : (
                      <Check className="w-3.5 h-3.5" aria-hidden="true" />
                    )}
                    {isSaving ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : (
                <>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={handleStartEdit}
                      aria-label="Edit report"
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold bg-surface text-fg border border-border-default hover:bg-bg-warm hover:border-border-strong transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                      Edit
                    </button>
                  )}
                  {canExport && (
              <div ref={exportMenuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setIsExportMenuOpen((open) => !open)}
                  disabled={isAnyExporting}
                  aria-label="Export report"
                  aria-haspopup="menu"
                  aria-expanded={isExportMenuOpen}
                  title={lastExportError ?? 'Download report'}
                  className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold transition-colors
                    ${isAnyExporting
                      ? 'bg-surface text-fg-tertiary border border-border-default cursor-wait'
                      : isExportMenuOpen
                        ? 'bg-bg-warm text-fg border border-border-strong'
                        : 'bg-surface text-fg border border-border-default hover:bg-bg-warm hover:border-border-strong'}
                  `}
                >
                  <Download className={`w-3.5 h-3.5 ${isAnyExporting ? 'animate-pulse' : ''}`} aria-hidden="true" />
                  {isAnyExporting ? 'Exporting…' : 'Export'}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${isExportMenuOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                {isExportMenuOpen && (
                  <div
                    role="menu"
                    aria-label="Export format"
                    className="absolute right-0 top-full mt-1 min-w-[180px] bg-surface border border-border-default rounded-md shadow-bark-md py-1 z-10"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setIsExportMenuOpen(false); handleExportPdf(); }}
                      className="w-full inline-flex items-center gap-2 px-3 py-2 text-left text-xs font-medium text-fg hover:bg-bg-warm transition-colors"
                    >
                      <Download className="w-3.5 h-3.5 text-fg-secondary" aria-hidden="true" />
                      <span>PDF</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setIsExportMenuOpen(false); handleExportDocx(); }}
                      className="w-full inline-flex items-center gap-2 px-3 py-2 text-left text-xs font-medium text-fg hover:bg-bg-warm transition-colors"
                    >
                      <FileText className="w-3.5 h-3.5 text-fg-secondary" aria-hidden="true" />
                      <span>Word (.docx)</span>
                    </button>
                    {/*
                      PowerPoint export is built end-to-end (backend renderer +
                      `handleExportPptx`/`exportWorkspaceReportPptx`), but the menu
                      item is intentionally DISABLED pending final design tweaks to
                      the generated deck. To ship: remove `disabled`/`aria-disabled`
                      and the `soon` badge below. Keeping the onClick wired (it can't
                      fire while disabled) means re-enabling is a one-line change and
                      the handler/state stay referenced. See PR #200.
                    */}
                    <button
                      type="button"
                      role="menuitem"
                      disabled
                      aria-disabled="true"
                      onClick={() => { setIsExportMenuOpen(false); handleExportPptx(); }}
                      title="PowerPoint export — coming soon (in final design tweaks)"
                      className="w-full inline-flex items-center gap-2 px-3 py-2 text-left text-xs font-medium text-fg-tertiary cursor-not-allowed transition-colors"
                    >
                      <Presentation className="w-3.5 h-3.5 text-fg-tertiary opacity-50" aria-hidden="true" />
                      <span>PowerPoint (.pptx)</span>
                      <span className="ml-auto text-[10px] font-mono font-semibold bg-bg-warm text-fg-tertiary border border-border-default px-1.5 py-0.5 rounded-full uppercase tracking-wider">soon</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setIsExportMenuOpen(false); handleOpenInGoogleDocs(); }}
                      title="Downloads .docx and opens Google Docs — drag the file in to convert"
                      className="w-full inline-flex items-center gap-2 px-3 py-2 text-left text-xs font-medium text-fg hover:bg-bg-warm transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5 text-fg-secondary" aria-hidden="true" />
                      <span>Google Docs</span>
                    </button>
                  </div>
                )}
              </div>
                  )}
                </>
              )}
            </div>
          )}
          {report?.title && (
            <div className="flex justify-between items-start gap-3 mb-6 pb-3 border-b border-border-default">
              <h1 className="text-xl font-bold text-fg">
                {report.title}
              </h1>
              {/* BAR-192 Phase C — validation badge (placement: right of
                  title; alternatives live in the plan for preview review).
                  Token tiers: neutral badge / warning / success. The
                  sub-line states explicitly that semantic validation HAS
                  run on this version (second-soak feedback) — and whether
                  the verdicts were carried across an edit. */}
              <span className="shrink-0 inline-flex flex-col items-end mt-1">
                <span className="inline-flex items-center gap-1.5">
                  {reviewable && reviewProgress.decidable > 0 ? (
                    <button
                      type="button"
                      data-testid="grounding-review-button"
                      onClick={() => setReviewOpen(true)}
                      className="inline-flex items-center h-[22px] px-2 rounded text-xs font-medium border border-border-default text-fg-secondary hover:bg-neutral-warm whitespace-nowrap"
                      title="Review what validation found — accept or reject each verdict."
                    >
                      Review{reviewProgress.awaiting > 0 ? ` (${reviewProgress.awaiting})` : ''}
                    </button>
                  ) : null}
                  <span
                    data-testid="grounding-badge"
                    title={sealBadge.tip}
                    className={`inline-flex items-center h-[22px] px-2 rounded text-xs font-medium whitespace-nowrap ${sealBadge.cls}`}
                  >
                    {SealBadgeIcon && (
                      <SealBadgeIcon
                        size={16}
                        strokeWidth={1.5}
                        className="mr-1"
                        aria-hidden="true"
                      />
                    )}
                    {sealBadge.label}
                  </span>
                </span>
                {sealBadge.sub && (
                  <span
                    data-testid="grounding-badge-subline"
                    className="text-[10px] text-fg-tertiary mt-0.5 whitespace-nowrap"
                  >
                    {sealBadge.sub}
                  </span>
                )}
                {workReviewLine && (
                  <span
                    data-testid="validate-work-line"
                    title={workReviewLine.tip}
                    className={`text-[10px] mt-0.5 whitespace-nowrap ${workReviewLine.cls}`}
                  >
                    {workReviewLine.text}
                    {reviewFlags.length > 0 && (
                      <button
                        type="button"
                        data-testid="validate-work-flags-toggle"
                        onClick={() => setShowReviewFlags((v) => !v)}
                        className="ml-1.5 inline-flex items-center min-h-8 px-1.5 -my-2 align-middle underline underline-offset-2 text-fg-secondary hover:text-fg"
                      >
                        {showReviewFlags ? 'hide' : `show ${reviewFlags.length}`}
                      </button>
                    )}
                  </span>
                )}
                {showReviewFlags && reviewFlags.length > 0 && (
                  <ol
                    data-testid="validate-work-flags"
                    className="mt-1.5 mb-1 max-w-[42rem] text-[11px] leading-snug text-fg-secondary space-y-1.5 list-decimal pl-4"
                  >
                    {reviewFlags.map((f) => (
                      <li key={f.key}>
                        <span className={`font-semibold ${f.verdict === 'rejected' ? 'text-danger' : 'text-fg'}`}>
                          {f.name}
                        </span>
                        <span className="text-fg-tertiary"> · {f.verdict}</span>
                        <div className="whitespace-pre-wrap">{f.rationale}</div>
                      </li>
                    ))}
                  </ol>
                )}
                {/* BAR-192 auto-repair: the legible trail (R5) — what the
                    in-call loop fixed, judged minor, and left for the human. */}
                {autoRepair && autoRepair.found > 0 && (
                  <span
                    data-testid="auto-repair-summary"
                    className="text-[10px] text-fg-tertiary mt-0.5 whitespace-nowrap"
                  >
                    Auto-repair: fixed {autoRepair.auto_fixed} of {autoRepair.found}
                    {autoRepair.nits > 0 ? `, ${autoRepair.nits} judged minor` : ''}
                    {autoRepair.material_remaining > 0
                      ? `, ${autoRepair.material_remaining} need you`
                      : ''}
                  </span>
                )}
              </span>
            </div>
          )}
          <div className="report-content max-w-none">

          {isEditing ? (
            <ReportEditor
              ref={editorRef}
              initialContent={localContent ?? ''}
              onChange={handleEditorChange}
            />
          ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={{
              // Shared base renderer (citation chips, tables, headings, lists,
              // …) — identical to the documentation surface. ReportView adds
              // only the BAR-192 grounding overlays on `p`/`li` below.
              ...baseComponents,
              p: ({ node, children }) => {
                // BAR-192 Phase C — suspected-amber highlight on flagged blocks
                const found = groundingForNode(node as any);
                const hl = groundingClass(found);
                return (
                  <p
                    className={`text-sm leading-relaxed text-fg-secondary mb-3${hl ? ` ${hl} px-3 py-2 pr-9 relative` : ''}`}
                  >
                    {children}
                    {hl && found ? (
                      <GroundingFlag
                        findings={found}
                        onDecide={reviewable ? (d) => handleDecisions([d]) : undefined}
                        onFix={onDispatchChatMessage ? handleFix : undefined}
                        onFindSource={onDispatchChatMessage ? handleFindSource : undefined}
                        onInternal={reviewable ? handleInternal : undefined}
                        onValidate={onDispatchChatMessage ? handleValidate : undefined}
                        validatePending={validatePending}
                        submitting={reviewSubmitting}
                      />
                    ) : null}
                  </p>
                );
              },
              li: ({ node, children }) => {
                // Loose lists (blank lines between items — the common shape
                // for "- [ ] REQ" checklists) wrap the item text in an inner
                // <p> that STARTS ON THE SAME source line as the li. Both
                // would match the flagged line and render two nested amber
                // boxes (the "box under the box" bug) — so when a paragraph
                // child on the same line exists, the paragraph carries the
                // highlight alone and the li stays unstyled. Tight lists
                // (no p child) keep the highlight on the li itself.
                const found = groundingForNode(node as any);
                const liLine = (node as any)?.position?.start?.line;
                const hasSameLineParagraph = ((node as any)?.children ?? []).some(
                  (c: any) =>
                    c?.type === 'element' &&
                    c?.tagName === 'p' &&
                    c?.position?.start?.line === liLine,
                );
                const hl = hasSameLineParagraph ? '' : groundingClass(found);
                return (
                  <li
                    className={`text-sm text-fg-secondary leading-relaxed${hl ? ` ${hl} px-2 py-1 pr-9 relative` : ''}`}
                  >
                    {children}
                    {hl && found ? (
                      <GroundingFlag
                        findings={found}
                        onDecide={reviewable ? (d) => handleDecisions([d]) : undefined}
                        onFix={onDispatchChatMessage ? handleFix : undefined}
                        onFindSource={onDispatchChatMessage ? handleFindSource : undefined}
                        onInternal={reviewable ? handleInternal : undefined}
                        onValidate={onDispatchChatMessage ? handleValidate : undefined}
                        validatePending={validatePending}
                        submitting={reviewSubmitting}
                      />
                    ) : null}
                  </li>
                );
              },
            }}
          >
            {processedContent}
          </ReactMarkdown>
          )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={discardDialogOpen}
        onCancel={() => setDiscardDialogOpen(false)}
        onConfirm={discardEdits}
        title="Discard unsaved report edits?"
        description="Your changes to this report will be lost."
        confirmLabel="Discard"
        variant="destructive"
      />
    </div>
  );
}
